import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Webhook, Pencil, Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';
import { shopifyApi } from '@/lib/api/services/shopify';
import { API_CONFIG } from '@/lib/api/config';


const SUPPORTED_WEBHOOK_TOPICS = [
  { topic: 'orders/create', label: 'Order Created', description: 'When a new order is placed' },
  { topic: 'orders/updated', label: 'Order Updated', description: 'When an order is modified' },
  { topic: 'orders/paid', label: 'Order Paid', description: 'When payment is confirmed' },
  { topic: 'orders/cancelled', label: 'Order Cancelled', description: 'When an order is cancelled' },
  { topic: 'orders/delete', label: 'Order Deleted', description: 'Syncs order deletions' },
  { topic: 'checkouts/create', label: 'Checkout Created', description: 'Abandoned cart detection' },
  { topic: 'checkouts/update', label: 'Checkout Updated', description: 'Checkout status changes' },
  { topic: 'customers/create', label: 'Customer Created', description: 'When a new customer signs up' },
  { topic: 'orders/fulfilled', label: 'Order Fulfilled', description: 'When an order is fully fulfilled' },
  { topic: 'orders/partially_fulfilled', label: 'Order Partially Fulfilled', description: 'When an order is partially fulfilled' },
  { topic: 'refunds/create', label: 'Refund Created', description: 'When a refund is issued' },
  { topic: 'products/create', label: 'Product Created', description: 'Syncs new products' },
  { topic: 'products/update', label: 'Product Updated', description: 'Syncs product changes' },
  { topic: 'products/delete', label: 'Product Deleted', description: 'Syncs product deletions' },
  { topic: 'app/uninstalled', label: 'App Uninstalled', description: 'Cleanup when app is removed' },
];

export function ShopifyConfiguration({
  businessAccountId,
  active = true,
}: {
  businessAccountId: string | null | undefined;
  active?: boolean;
}) {
  const { toast } = useToast();
  const selectedAccountId = businessAccountId;

  const [webhooks, setWebhooks] = useState<Array<{ id: number; address: string; topic: string; created_at: string; updated_at: string; format: string; api_version: string }>>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhookCreating, setWebhookCreating] = useState<string | null>(null);
  const [webhookDeleting, setWebhookDeleting] = useState<number | null>(null);
  const [registerAllLoading, setRegisterAllLoading] = useState(false);
  const [editingTopic, setEditingTopic] = useState<string | null>(null);
  const [topicUrls, setTopicUrls] = useState<Record<string, string>>({});

  const defaultWebhookUrl = useMemo(() => {
    const base = import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL;
    const origin = base.replace(/\/api\/v1\/?$/, '');
    return `${origin}/api/v1/shopify/webhooks`;
  }, []);

  const loadWebhooks = async () => {
    if (!selectedAccountId) return;
    setWebhooksLoading(true);
    try {
      const list = await shopifyApi.listWebhooks(selectedAccountId);
      setWebhooks(list);
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to load webhooks' } });
    } finally {
      setWebhooksLoading(false);
    }
  };

  const getTopicUrl = (topic: string) => topicUrls[topic] ?? topicUrls['__default__'] ?? defaultWebhookUrl;
  const setTopicUrl = (topic: string, url: string) => setTopicUrls(prev => ({ ...prev, [topic]: url }));

  const handleCreateWebhook = async (topic: string) => {
    if (!selectedAccountId) return;
    const address = getTopicUrl(topic);
    if (!address) {
      toast({ title: 'Enter a webhook URL first', variant: 'warning' });
      return;
    }
    setWebhookCreating(topic);
    try {
      await shopifyApi.createWebhook(selectedAccountId, topic, address);
      toast({ variant: 'success', title: `Webhook registered: ${topic}` });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to create webhook' } });
    } finally {
      setWebhookCreating(null);
    }
  };

  const handleUpdateWebhook = async (topic: string, webhookId: number) => {
    if (!selectedAccountId) return;
    const address = getTopicUrl(topic);
    if (!address) {
      toast({ title: 'Enter a webhook URL first', variant: 'warning' });
      return;
    }
    setWebhookCreating(topic);
    try {
      await shopifyApi.updateWebhook(selectedAccountId, webhookId, address);
      toast({ variant: 'success', title: `Webhook updated: ${topic}` });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to update webhook' } });
    } finally {
      setWebhookCreating(null);
    }
  };

  const handleDeleteWebhook = async (webhookId: number) => {
    if (!selectedAccountId) return;
    setWebhookDeleting(webhookId);
    try {
      await shopifyApi.deleteWebhook(selectedAccountId, webhookId);
      toast({ variant: 'success', title: 'Webhook removed' });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to delete webhook' } });
    } finally {
      setWebhookDeleting(null);
    }
  };

  const handleRegisterAll = async () => {
    if (!selectedAccountId) return;
    const targetUrl = getTopicUrl('__default__');
    if (!targetUrl) {
      toast({ title: 'Enter a webhook URL first', variant: 'warning' });
      return;
    }
    setRegisterAllLoading(true);
    let count = 0;
    let failed = 0;
    try {
      const allWebhooks = await shopifyApi.listWebhooks(selectedAccountId);

      for (const { topic } of SUPPORTED_WEBHOOK_TOPICS) {
        const existing = allWebhooks.find(w => w.topic === topic);
        try {
          if (!existing) {
            await shopifyApi.createWebhook(selectedAccountId, topic, targetUrl);
            count++;
          } else if (existing.address !== targetUrl) {
            await shopifyApi.deleteWebhook(selectedAccountId, existing.id);
            await shopifyApi.createWebhook(selectedAccountId, topic, targetUrl);
            count++;
          }
        } catch {
          failed++;
        }
      }
      const parts = [];
      if (count) parts.push(`${count} registered`);
      if (failed) parts.push(`${failed} skipped (permission issues)`);
      toast({ title: parts.length ? parts.join(', ') : 'All webhooks already up to date' });
      await loadWebhooks();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to register webhooks' } });
    } finally {
      setRegisterAllLoading(false);
    }
  };

  useEffect(() => {
    if (active && selectedAccountId) {
      loadWebhooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selectedAccountId]);

  useEffect(() => {
    if (webhooks.length > 0) {
      setTopicUrls(prev => {
        const next = { ...prev };
        for (const w of webhooks) {
          if (!next[w.topic]) next[w.topic] = w.address;
        }
        return next;
      });
    }
  }, [webhooks]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          value={getTopicUrl('__default__')}
          onChange={(e) => setTopicUrl('__default__', e.target.value)}
          placeholder="https://your-backend.com/api/v1/shopify/webhooks"
          className="flex-1 font-mono text-sm h-9"
        />
        <Button
          size="sm"
          onClick={handleRegisterAll}
          disabled={registerAllLoading || webhooksLoading || !getTopicUrl('__default__')}
          className="bg-[#95BF47] hover:bg-[#7da03b] text-white h-9 shrink-0"
        >
          {registerAllLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Webhook className="h-4 w-4 mr-1.5" />}
          Register All
        </Button>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={loadWebhooks} disabled={webhooksLoading}>
          <RefreshCw className={`h-4 w-4 ${webhooksLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        {webhooksLoading && webhooks.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading webhooks from Shopify...
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b">
                <th className="text-left font-medium text-muted-foreground text-xs px-4 py-2.5 w-10">Status</th>
                <th className="text-left font-medium text-muted-foreground text-xs px-4 py-2.5">Event</th>
                <th className="text-left font-medium text-muted-foreground text-xs px-4 py-2.5">Webhook URL</th>
                <th className="text-right font-medium text-muted-foreground text-xs px-4 py-2.5 w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {SUPPORTED_WEBHOOK_TOPICS.map(({ topic, label }) => {
                const registered = webhooks.find(w => w.topic === topic);
                const isCreating = webhookCreating === topic;
                const isDeleting = registered ? webhookDeleting === registered.id : false;
                const isEditing = editingTopic === topic;
                const currentUrl = getTopicUrl(topic);
                const urlChanged = registered && currentUrl !== registered.address;

                return (
                  <tr key={topic} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2.5">
                      <div className={`w-2.5 h-2.5 rounded-full mx-auto ${registered ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-sm">{label}</div>
                      <code className="text-[10px] font-mono text-muted-foreground">{topic}</code>
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <Input
                            autoFocus
                            value={currentUrl}
                            onChange={(e) => setTopicUrl(topic, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setEditingTopic(null);
                                if (registered && urlChanged) handleUpdateWebhook(topic, registered.id);
                                else if (!registered) handleCreateWebhook(topic);
                              }
                              if (e.key === 'Escape') setEditingTopic(null);
                            }}
                            placeholder="https://..."
                            className="font-mono text-xs h-7 flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-green-600 hover:text-green-700"
                            onClick={() => {
                              setEditingTopic(null);
                              if (registered && urlChanged) handleUpdateWebhook(topic, registered.id);
                              else if (!registered) handleCreateWebhook(topic);
                            }}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <span
                          className="font-mono text-xs text-muted-foreground truncate block max-w-md cursor-pointer hover:text-foreground transition-colors"
                          onClick={() => setEditingTopic(topic)}
                          title={registered ? registered.address : 'Click to set URL'}
                        >
                          {registered ? registered.address : <span className="italic text-muted-foreground/60">Not registered</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {registered ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setEditingTopic(editingTopic === topic ? null : topic)}
                              title="Edit URL"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteWebhook(registered.id)}
                              disabled={isDeleting}
                              title="Delete webhook"
                            >
                              {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleCreateWebhook(topic)}
                            disabled={isCreating}
                          >
                            {isCreating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                            Register
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {webhooks.filter(w => !SUPPORTED_WEBHOOK_TOPICS.some(t => t.topic === w.topic)).map(w => (
                <tr key={w.id} className="bg-muted/10 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="w-2.5 h-2.5 rounded-full mx-auto bg-blue-400" />
                  </td>
                  <td className="px-4 py-2.5">
                    <code className="text-xs font-mono">{w.topic}</code>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-xs text-muted-foreground truncate block max-w-md">{w.address}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteWebhook(w.id)}
                        disabled={webhookDeleting === w.id}
                      >
                        {webhookDeleting === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ShopifyConfiguration;
